import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../global/TopBar';
import { ShoppingBagIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const storeCategories = [
  {
    id: 'groceries',
    name: 'Groceries',
    description: 'Fresh produce, pantry staples, and household essentials',
    icon: ShoppingBagIcon,
    image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80',
    color: 'from-green-400 to-emerald-600'
  },
  {
    id: 'jewelry',
    name: 'Jewelry',
    description: 'Elegant jewelry pieces for every occasion',
    icon: SparklesIcon,
    image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80',
    color: 'from-pri-yellow to-amber-600'
  },
  {
    id: 'clothes',
    name: 'Clothes',
    description: 'Trendy and comfortable clothing for all styles',
    icon: SparklesIcon,
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80',
    color: 'from-blue-400 to-indigo-600'
  },
];

const Store = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  useEffect(() => {
    let interval;
    if (!hoveredCategory) {
      interval = setInterval(() => {
        setSelectedCategory(prev => {
          const currentIndex = storeCategories.findIndex(cat => cat.id === prev);
          const nextIndex = (currentIndex + 1) % storeCategories.length;
          return storeCategories[nextIndex].id;
        });
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [hoveredCategory]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark">
      <TopBar />
      <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h1 
            className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pri-yellow to-pri-green text-transparent bg-clip-text"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Explore Our Stores
          </motion.h1>
          <motion.p 
            className="text-lg md:text-xl text-pri-gray max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Discover a world of quality products across various categories
          </motion.p>
        </motion.div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {storeCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              onHoverStart={() => setHoveredCategory(category.id)}
              onHoverEnd={() => setHoveredCategory(null)}
              whileHover={{ y: -10 }}
            >
              <Link
                to={`/store/${category.id}`}
                className="block h-full"
              >
                <motion.div 
                  className="bg-sec-dark/50 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg h-full"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative h-64 overflow-hidden">
                    <motion.img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <motion.div 
                      className={`absolute top-4 left-4 bg-gradient-to-r ${category.color} p-3 rounded-xl shadow-lg`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <category.icon className="h-6 w-6 text-white" />
                    </motion.div>
                  </div>
                  
                  <div className="p-6">
                    <h2 className="text-2xl font-bold mb-2 text-pri-white">{category.name}</h2>
                    <p className="text-pri-gray mb-4">{category.description}</p>
                    <motion.div
                      className="flex items-center text-pri-yellow"
                      whileHover={{ x: 10 }}
                    >
                      <span className="font-semibold mr-2">Explore Now</span>
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        →
                      </motion.span>
                    </motion.div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Featured Section */}
        <motion.div 
          className="bg-sec-dark/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.h2 
            className="text-3xl font-bold mb-8 text-center bg-gradient-to-r from-pri-yellow to-pri-green text-transparent bg-clip-text"
          >
            Featured Category
          </motion.h2>
          
          <AnimatePresence mode="wait">
            {selectedCategory && (
              <motion.div
                key={selectedCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                <motion.div
                  className="max-w-2xl mx-auto"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-2xl font-bold mb-4 text-pri-white">
                    {storeCategories.find(cat => cat.id === selectedCategory).name}
                  </h3>
                  <p className="text-pri-gray mb-6">
                    {storeCategories.find(cat => cat.id === selectedCategory).description}
                  </p>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link 
                      to={`/store/${selectedCategory}`}
                      className="inline-block bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark px-8 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Shop Now
                    </Link>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Store;
